import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { commonContainerWrapper } from "../../Common/styles/flex";
import { ErrorState, PanelLoadingState } from "../Common/StateViews";
import { ReasonDialog } from "../Common/ReasonDialog";
import { newIdempotencyKey } from "../Common/idempotency";
import { useRandomizationAccess } from "../useRandomizationAccess";
import {
  useAllocateParticipantMutation,
  useGetAllocationHistoryQuery,
  useGetAllocationQuery,
  useGetParticipantFactorsQuery,
  useReverseAllocationMutation,
  useUnblindParticipantMutation,
} from "../../../Redux/api/randomizationApi";
import { FactorReadinessTable } from "./FactorReadinessTable";
import { allocationRefusal } from "./allocationRefusal";
import {
  randomizationErrorKey,
  randomizationErrorMessage,
  showRandomizationError,
  showRandomizationSuccess,
} from "../Common/serverErrors";

/**
 * The participant randomization panel. Spec 21.3.
 *
 * ONE COMPONENT, TWO CALLERS. The participant record renders it with
 * `triggerSource = "participant_record"`; the `randomize` question type inside
 * a form renders the SAME component with `triggerSource = "form_question"`
 * (D26, spec 21.3). Two implementations would be two sets of preconditions and
 * two confirmation dialogs, and the one in the form is the one nobody would
 * remember to update.
 *
 * THE UNBLINDING RESULT IS DELIBERATELY HELD IN LOCAL STATE AND NOWHERE ELSE.
 * The arm comes back once, is shown in a dialog, and is not written into the
 * RTK Query cache — because a cached arm survives a re-render and a route
 * change and would still be on screen after a refresh, which is exactly the
 * persistent visibility spec 11.3 forbids. Closing the dialog discards it;
 * seeing it again requires another audited unblinding.
 *
 * THE INELIGIBLE STATE SHOWS THE REASON THE SERVER GAVE, not a generic "cannot
 * randomize". "This participant cannot be randomized yet because gender has
 * not been recorded" tells a coordinator what to do; the generic version sends
 * them to support.
 *
 * @category Randomization
 */

export interface RandomizationPanelProps {
  studyId: string;
  participantId: string;
  /** Shown in the confirmation dialog so the user can see who they are about
   *  to randomize. */
  subjectId?: string | null;
  triggerSource: "participant_record" | "form_question";
  /** Renders without the outer card, for embedding inside a form question. */
  embedded?: boolean;
  onAllocated?: (result: {
    allocationId: string | null;
    randomizationNumber: number | null;
  }) => void;
}

/**
 * THESE MUST MATCH THE SERVER, AND DID NOT.
 *
 * Both lists are `@IsIn`-validated in the request DTOs, and the unblinding
 * codes are additionally constrained in the database by
 * `randomization_unblinding_events_reason_chk`. This file previously carried
 * a third, independent vocabulary - `data_entry_error`, `adverse_event`,
 * `safety_review` and the rest - so most of what the dropdown offered was
 * rejected the moment it was submitted, after the user had already typed a
 * ten-character justification.
 *
 * Source of truth, in order: the CHECK constraint, then the DTO, then this.
 * See the note in `unblind-participant.dto.ts`.
 */
const REVERSAL_REASON_CODES = [
  "entered_in_error",
  "wrong_participant",
  "ineligible_after_randomization",
  "consent_withdrawn",
  "other",
];

const UNBLIND_REASON_CODES = [
  "safety_emergency",
  "susar_report",
  "regulatory_request",
  "treatment_decision",
  "other",
];

/** The one code the database will not store without a safety reference. */
const UNBLIND_CODE_REQUIRING_SAFETY_REFERENCE = "susar_report";

export const RandomizationPanel = ({
  studyId,
  participantId,
  subjectId,
  triggerSource,
  embedded = false,
  onAllocated,
}: RandomizationPanelProps) => {
  const { t } = useTranslation("randomization");
  const access = useRandomizationAccess();

  const allocation = useGetAllocationQuery(
    { studyId, participantId },
    { skip: !studyId || !participantId || !access.canViewAllocation },
  );
  const history = useGetAllocationHistoryQuery(
    { studyId, participantId },
    { skip: !studyId || !participantId || !access.canViewAllocation },
  );
  /*
   * THE FACTORS ARE A SEPARATE REQUEST FROM THE ALLOCATION, and gated on
   * `settings_view` rather than `allocation_view`.
   *
   * They answer a different question. The allocation says what a participant
   * was given; this says which questions the design depends on and whether
   * they are in — which a coordinator who may randomize but may not see arms
   * still has to be able to read, or they cannot tell why the button is
   * disabled.
   */
  const factorStatus = useGetParticipantFactorsQuery(
    { studyId, participantId },
    { skip: !studyId || !participantId || !access.canViewSettings },
  );

  const [allocate, allocateState] = useAllocateParticipantMutation();
  const [reverse, reverseState] = useReverseAllocationMutation();
  const [unblind, unblindState] = useUnblindParticipantMutation();

  const [confirmRandomize, setConfirmRandomize] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [unblindOpen, setUnblindOpen] = useState(false);
  /** Held here and only here. Never cached. See the class comment. */
  const [disclosedArm, setDisclosedArm] = useState<string | null>(null);

  if (!access.featureEnabled || !access.canViewAllocation) return null;
  if (allocation.isLoading) return <PanelLoadingState />;

  const wrapper = embedded ? {} : { ...commonContainerWrapper };
  const data = allocation.data;

  if (allocation.isError) {
    /*
     * NOT_CONFIGURED IS NOT A FAULT, and a red error panel on a participant
     * record says it is.
     *
     * `findForParticipant` answers 404 `randomization.NOT_CONFIGURED` when the
     * study has no randomization configuration at all - which is the normal
     * state of every study between enabling the feature and committing a
     * design. The panel rendered `ErrorState` with a Retry button for it, so a
     * coordinator opening any participant record on such a study saw what
     * looked like a broken screen, and retrying could never help.
     *
     * Shown as a quiet line instead: the feature IS on, so saying nothing
     * would be its own puzzle.
     */
    if (randomizationErrorKey(allocation.error) === "NOT_CONFIGURED") {
      return (
        <Box sx={wrapper}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {t("ui.participant.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("ui.participant.notConfigured")}
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={wrapper}>
        <ErrorState error={allocation.error} onRetry={() => allocation.refetch()} />
      </Box>
    );
  }

  const handleAllocate = async () => {
    try {
      const result = await allocate({
        studyId,
        participantId,
        triggerSource,
        // A fresh key per attempt: the same key against a different request is
        // refused by the server on purpose, so a page-scoped key could not
        // answer the second participant with the first one's allocation.
        idempotencyKey: newIdempotencyKey(),
      }).unwrap();
      setConfirmRandomize(false);
      showRandomizationSuccess(t("ALLOCATED"));
      onAllocated?.({
        allocationId: result.allocationId,
        randomizationNumber: result.randomizationNumber,
      });
    } catch (error) {
      showRandomizationError(error, t as never);
    }
  };

  const row = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: "flex", py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 200 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Box>
  );

  const randomized = data?.isRandomized === true;

  return (
    <Box sx={wrapper}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {t("ui.participant.title")}
      </Typography>

      {!randomized ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t("ui.participant.notRandomized")}
          </Typography>

          {/*
            THE TABLE IS ON THE PANEL, NOT ONLY IN THE DIALOG. A coordinator
            looking at a participant record needs to see why the button is
            disabled without pressing it, and the dialog only opens for someone
            who can already act.
          */}
          <Box sx={{ mb: 2 }}>
            <FactorReadinessTable
              status={factorStatus.data}
              loading={factorStatus.isLoading}
            />
          </Box>

          {access.canAllocate ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => setConfirmRandomize(true)}
              /*
                DISABLED UNTIL EVERY FACTOR IS ANSWERED, and the table above
                says which one is not. This replaces a press that returns
                STRATUM_UNRESOLVED — an error naming nothing — with a button
                that visibly cannot be pressed yet and a row that says why.

                Not disabled while the readiness request is still in flight and
                undecided: `ready` is only trusted once it has arrived.
              */
              disabled={
                allocateState.isLoading ||
                factorStatus.isLoading ||
                factorStatus.data?.ready === false
              }
            >
              {t("ui.participant.randomize")}
            </Button>
          ) : null}
          {/*
            An ACTION that was refused, not a screen that failed to load.
            `ErrorState` is titled "Randomization could not be loaded", which
            for a refused allocation is simply untrue - and it offers a Retry
            that repeats a request the server has already answered. The
            server's own message says what to do.
          */}
          {allocateState.error ? (
            (() => {
              /*
                FOUR REFUSALS, FOUR REMEDIES, FOUR DIFFERENT PEOPLE WHO FIX
                THEM. A single "could not randomize" makes them look alike:
                a missing answer is the site's, a missing sequence and an
                inactive version are the study designer's, and an exhausted
                list is whoever may extend it. See `allocationRefusal`.
              */
              const refusal = allocationRefusal(allocateState.error);
              return (
                <Alert severity={refusal.severity} sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {refusal.key
                      ? t(`ui.participant.refusal.${refusal.key}.title`)
                      : t("ui.participant.refusal.generic.title")}
                  </Typography>
                  <Typography variant="body2">
                    {randomizationErrorMessage(allocateState.error, t as never)}
                  </Typography>
                  {refusal.key ? (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      {t(`ui.participant.refusal.${refusal.key}.remedy`)}
                    </Typography>
                  ) : null}
                </Alert>
              );
            })()
          ) : null}
        </Box>
      ) : (
        <Box>
          {row(t("ui.participant.number"), data?.randomizationNumber ?? "—")}
          {row(
            t("ui.participant.date"),
            data?.allocatedAt ? new Date(data.allocatedAt).toLocaleString() : "—",
          )}
          {row(
            t("ui.participant.stratum"),
            // The LABEL. `stratumKey` is `age=A2|gender=G1|site=<uuid>`, and
            // this panel is the screen a real-study demo lives in - it read
            // `site=1277eeb9-...` until 10 Sep 2026. Falls back to the key
            // when `randomization.stratum_view` is not held, which is when no
            // label is sent.
            data?.stratumLabel ?? data?.stratumKey ?? "—",
          )}
          {/* SF-D25. Which version produced this allocation — the label above
              is resolved through THAT version's map, not the study's latest. */}
          {row(
            t("ui.participant.configVersion"),
            data?.configVersion != null ? `v${data.configVersion}` : "—",
          )}
          {row(t("ui.participant.triggerSource"), data?.triggerSource ?? "—")}
          {row(t("ui.participant.attempt"), data?.attemptSeq ?? "—")}
          {/*
            `armCode` is ABSENT from the payload when the requester may not see
            it (spec 11.2), so this renders the arm or an explicit "hidden by
            the blind" — never an empty value, which reads as a data problem.
          */}
          {row(
            t("ui.participant.arm"),
            data?.armCode ? (
              <Chip size="small" label={data.armCode} color="primary" />
            ) : (
              <Typography variant="body2" color="text.secondary" component="span">
                {t("ui.participant.armHidden")}
              </Typography>
            ),
          )}
          {data?.unblindedAt ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {t("ui.participant.unblindedAlready", {
                date: new Date(data.unblindedAt).toLocaleString(),
              })}
            </Alert>
          ) : null}

          {/* The snapshot, not today's answers. See FactorReadinessTable. */}
          <Box sx={{ mt: 2 }}>
            <FactorReadinessTable
              status={factorStatus.data}
              loading={factorStatus.isLoading}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
            {/* Both not rendered without the permission (spec 21.8). */}
            {access.canReverse ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setReverseOpen(true)}
                disabled={reverseState.isLoading}
              >
                {t("ui.participant.reverse")}
              </Button>
            ) : null}
            {access.canUnblind ? (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={() => setUnblindOpen(true)}
                disabled={unblindState.isLoading}
              >
                {t("ui.participant.unblind")}
              </Button>
            ) : null}
          </Box>
        </Box>
      )}

      {history.data && history.data.length > 1 ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("ui.participant.history")}
          </Typography>
          {history.data.map((entry) => (
            <Box
              key={entry.allocationId ?? `${entry.attemptSeq}`}
              sx={{ display: "flex", gap: 2, py: 0.5 }}
            >
              <Typography variant="caption" sx={{ width: 40 }}>
                #{entry.attemptSeq}
              </Typography>
              <Typography variant="caption" sx={{ width: 80 }}>
                {entry.randomizationNumber ?? "—"}
              </Typography>
              <Typography variant="caption" sx={{ width: 90 }}>
                {entry.state ?? "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {/* The label, not the enum: the dropdowns were fixed on
                    10 Sep and this read `entered_in_error`. */}
                {entry.reversalReasonCode
                  ? t(`reasonCode.${entry.reversalReasonCode}`, {
                      defaultValue: entry.reversalReasonCode,
                    })
                  : ""}
                {/* Free text only for a holder of reason_view (spec 11.3). */}
                {entry.reversalReason ? ` — ${entry.reversalReason}` : ""}
              </Typography>
            </Box>
          ))}
        </>
      ) : null}

      {/* ------------------------------------------- randomize confirmation --- */}
      <Dialog
        open={confirmRandomize}
        onClose={allocateState.isLoading ? undefined : () => setConfirmRandomize(false)}
        aria-labelledby="randomize-confirm-title"
      >
        <DialogTitle id="randomize-confirm-title">
          {subjectId
            ? t("ui.participant.randomizeTitle", { subjectId })
            : t("ui.participant.randomizeTitleNoSubject")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t("ui.participant.randomizeBody")}</DialogContentText>

          {/*
            RESTATED IN THE DIALOG, on purpose. This is the last screen before
            a randomization number is issued and it cannot be taken back, so
            the stratification the participant is about to be placed in is
            shown one more time rather than remembered from the panel behind.
          */}
          <Box sx={{ mt: 2 }}>
            <FactorReadinessTable
              status={factorStatus.data}
              loading={factorStatus.isLoading}
            />
          </Box>

          {data?.stratumKey ? (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {t("ui.participant.randomizeStratum", {
                stratum: data.stratumLabel ?? data.stratumKey,
              })}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmRandomize(false)}
            disabled={allocateState.isLoading}
            color="inherit"
          >
            {t("ui.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAllocate}
            disabled={allocateState.isLoading}
          >
            {t("ui.participant.randomizeConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------------- reverse --- */}
      <ReasonDialog
        open={reverseOpen}
        title={t("ui.participant.reverseTitle")}
        body={t("ui.participant.reverseBody")}
        confirmLabel={t("ui.participant.reverseConfirm")}
        reasonLabel={t("ui.participant.reverseReason")}
        reasonCodes={REVERSAL_REASON_CODES.map((code) => ({
          value: code,
          label: t(`reasonCode.${code}`, { defaultValue: code }),
        }))}
        submitting={reverseState.isLoading}
        errorMessage={
          reverseState.error ? randomizationErrorMessage(reverseState.error, t as never) : null
        }
        onCancel={() => setReverseOpen(false)}
        onConfirm={async ({ reason, reasonCode }) => {
          try {
            await reverse({
              studyId,
              participantId,
              reasonCode: reasonCode ?? "other",
              reason,
              idempotencyKey: newIdempotencyKey(),
            }).unwrap();
            setReverseOpen(false);
            showRandomizationSuccess(t("REVERSED"));
          } catch (error) {
            showRandomizationError(error, t as never);
          }
        }}
      />

      {/* ---------------------------------------------------------- unblind --- */}
      <ReasonDialog
        open={unblindOpen}
        title={t("ui.participant.unblindTitle")}
        body={t("ui.participant.unblindBody")}
        confirmLabel={t("ui.participant.unblindConfirm")}
        reasonLabel={t("ui.participant.unblindReason")}
        reasonCodes={UNBLIND_REASON_CODES.map((code) => ({
          value: code,
          label: t(`reasonCode.${code}`, { defaultValue: code }),
        }))}
        extraField={{
          label: t("ui.participant.unblindSafetyReference"),
          name: "safetyReference",
          // 120 is the column width and the DTO's @MaxLength; the dialog's
          // default of 200 would accept values the server refuses.
          maxLength: 120,
          helperText: t("ui.participant.unblindSafetyReferenceHelp"),
          requiredForReasonCode: UNBLIND_CODE_REQUIRING_SAFETY_REFERENCE,
        }}
        submitting={unblindState.isLoading}
        errorMessage={
          unblindState.error ? randomizationErrorMessage(unblindState.error, t as never) : null
        }
        onCancel={() => setUnblindOpen(false)}
        onConfirm={async ({ reason, reasonCode, extra }) => {
          try {
            const result = await unblind({
              studyId,
              participantId,
              reasonCode: reasonCode ?? "other",
              reason,
              ...(extra ? { safetyReference: extra } : {}),
              idempotencyKey: newIdempotencyKey(),
            }).unwrap();
            setUnblindOpen(false);
            // Local state only. Not the cache, not a ref that outlives the
            // dialog, not the URL.
            setDisclosedArm(result.armCode);
          } catch (error) {
            showRandomizationError(error, t as never);
          }
        }}
      />

      {/* ------------------------------------------- the one-time disclosure --- */}
      <Dialog
        open={Boolean(disclosedArm)}
        onClose={() => setDisclosedArm(null)}
        aria-labelledby="unblind-result-title"
      >
        <DialogTitle id="unblind-result-title">
          {t("ui.participant.unblindResultTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="h4" sx={{ my: 2, textAlign: "center" }}>
            {disclosedArm}
          </Typography>
          <DialogContentText>
            {t("ui.participant.unblindResultBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDisclosedArm(null)}>
            {t("ui.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RandomizationPanel;
